import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { PayablesTable } from '@/components/revfinops/PayablesTable'
import { ProtectedRevFinOpsRoute } from '@/components/revfinops/ProtectedRevFinOpsRoute'
import { listPayables, RevFinOpsPayable } from '@/lib/revfinops-api'
import { RefreshCw } from 'lucide-react'

/**
 * PayablesPage - RevFinOps payables queue management page
 *
 * Features:
 * - Protected admin-only route
 * - Fetches payables from API on mount
 * - Sorts payables by due date (soonest first)
 * - Displays payables in table with status badges
 * - Color-coded due dates (overdue, due soon, normal)
 * - Loading, error, and empty states
 * - Refresh functionality
 * - Retry on error
 * - Clickable rows navigate to payable details
 *
 * Route: /revfinops/payables
 */
export default function PayablesPage() {
  const navigate = useNavigate()

  const [payables, setPayables] = useState<RevFinOpsPayable[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  /**
   * Sort payables by due date (soonest first)
   */
  const sortPayablesByDueDate = (payables: RevFinOpsPayable[]): RevFinOpsPayable[] => {
    return [...payables].sort((a, b) => {
      const dateA = new Date(a.dueDate).getTime()
      const dateB = new Date(b.dueDate).getTime()
      return dateA - dateB // Ascending (soonest first)
    })
  }

  /**
   * Load payables from API
   */
  const loadPayables = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await listPayables()

      if (response.success && response.data) {
        // Sort payables by due date before setting state
        const sorted = sortPayablesByDueDate(response.data)
        setPayables(sorted)
      } else {
        setError(response.error?.message || 'Failed to load payables')
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Load payables on component mount
   */
  useEffect(() => {
    loadPayables()
  }, [loadPayables])

  /**
   * Handle manual refresh
   */
  const handleRefresh = async () => {
    setIsRefreshing(true)
    await loadPayables()
    setIsRefreshing(false)
  }

  /**
   * Handle retry after error
   */
  const handleRetry = () => {
    loadPayables()
  }

  /**
   * Handle row click - navigate to payable details
   */
  const handleRowClick = (payableId: string) => {
    navigate({ to: `/revfinops/payables/${payableId}` })
  }

  return (
    <ProtectedRevFinOpsRoute>
      
        <div className="space-y-6">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
                Payables Queue
              </h1>
              <p className="text-brand-gray-light font-body text-lg">
                Track pending approvals and payment schedules
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="btn-ghost flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Payables Table */}
          <div className="animate-fade-in-up">
            <PayablesTable
              payables={payables}
              loading={loading}
              error={error}
              onRetry={handleRetry}
              onRowClick={handleRowClick}
            />
          </div>
        </div>
      
    </ProtectedRevFinOpsRoute>
  )
}
